/** Match the natural height of a nearby product, including in rows containing only ads. */
class CollectionAdCard extends HTMLElement {
  #observer;
  #frame;

  connectedCallback() {
    const grid = this.closest('.product-grid');
    if (!grid) return;

    let observedContent;

    const update = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const width = this.parentElement.getBoundingClientRect().width;
        if (!width) return;

        const items = [...grid.children];
        const index = items.indexOf(this.parentElement);
        const products = items.filter((item) => item.hasAttribute('data-product-id'));
        // A two-column ad still uses the height of a single-column product card.
        const columnWidth = Number.parseFloat(getComputedStyle(grid).gridTemplateColumns) || width;
        const sameWidth = products.filter((item) => Math.abs(item.getBoundingClientRect().width - columnWidth) < 1);
        const candidates = sameWidth.length ? sameWidth : products;
        const nearest = candidates.reduce((best, item) => {
          return !best || Math.abs(items.indexOf(item) - index) < Math.abs(items.indexOf(best) - index)
            ? item
            : best;
        }, null);
        const content = nearest?.querySelector('.product-card__content');
        if (!content) return;

        if (content !== observedContent) {
          this.#observer.disconnect();
          this.#observer.observe(grid);
          this.#observer.observe(content);
          for (const child of content.children) this.#observer.observe(child);
          observedContent = content;
        }

        const style = getComputedStyle(content);
        const itemStyle = getComputedStyle(nearest);
        const number = (value) => Number.parseFloat(value) || 0;
        const children = [...content.children].filter((child) => getComputedStyle(child).display !== 'none');
        const height = children.reduce((total, child) => {
          const childStyle = getComputedStyle(child);
          return total + child.getBoundingClientRect().height
            + number(childStyle.marginTop) + number(childStyle.marginBottom);
        }, 0) + Math.max(0, children.length - 1) * number(style.rowGap)
          + number(style.paddingTop) + number(style.paddingBottom)
          + number(style.borderTopWidth) + number(style.borderBottomWidth)
          + number(itemStyle.paddingTop) + number(itemStyle.paddingBottom)
          + number(itemStyle.borderTopWidth) + number(itemStyle.borderBottomWidth);

        // Measuring children avoids a feedback loop when the product stretches to the ad's row height.
        this.parentElement.style.setProperty('--collection-ad-min-height', `${height}px`);
      });
    };

    this.#observer = new ResizeObserver(update);
    this.#observer.observe(grid);
    update();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('collection-ad-card')) {
  customElements.define('collection-ad-card', CollectionAdCard);
}
